// Public-documents page — visible to every authenticated CRM user
// regardless of role. Two groups:
//
//  1. Leitfäden & Schulungsunterlagen — sales/talk-track guides for
//     Makler & Call-Center (view in browser or download the PDF).
//     Currently: Einwandbehandlung (objection handling).
//
//  2. Geldwäsche-Pflichtformulare — the two AML compliance forms
//     (Checkliste GW/TF + KYC). These carry proper AcroForm fields so the
//     operator can fill them in any PDF reader, or use the step-by-step
//     "Online ausfüllen" wizard which generates the same filled PDF.

import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Download,
  FileText,
  ShieldCheck,
  ClipboardList,
  Sparkles,
  PencilLine,
  MessageSquareQuote,
  BookOpen,
} from 'lucide-react';

export const Route = createFileRoute('/_app/dokumente')({
  component: PublicDocumentsPage,
});

// A view/download-only guide (no fillable form, no online wizard).
interface GuideCard {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  pdfUrl: string;
  pdfName: string;
}

interface DocumentCard {
  id: 'checkliste' | 'kyc';
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  fillablePdfUrl: string;
  fillablePdfName: string;
  onlineRoute: '/dokumente/checkliste' | '/dokumente/kyc';
}

function PublicDocumentsPage() {
  const { t } = useTranslation();

  const guides: GuideCard[] = [
    {
      id: 'einwandbehandlung',
      title: t('docs.public.einwand.title', 'Einwandbehandlung — Leitfaden für Makler & Call-Center'),
      subtitle: t(
        'docs.public.einwand.subtitle',
        'Verkaufsgespräch · Akquise · 91 Einwände mit fertigen Antworten',
      ),
      description: t(
        'docs.public.einwand.body',
        'Geprüfte Formulierungen für die häufige Frage „Warum rufen mich mehrere Makler von 4M an?", 91 typische Einwände von Verkäufern mit passenden Antworten, die 5-Schritte-Grundformel und die wichtigsten Regeln für das Telefon.',
      ),
      icon: <MessageSquareQuote className="h-5 w-5" />,
      pdfUrl: '/docs/4M-Einwandbehandlung-DE.pdf',
      pdfName: '4M-Einwandbehandlung-DE.pdf',
    },
  ];

  const documents: DocumentCard[] = [
    {
      id: 'checkliste',
      title: t('docs.public.checkliste.title', 'Checkliste GW/TF — Risikobeurteilung'),
      subtitle: t(
        'docs.public.checkliste.subtitle',
        'Geldwäsche / Terrorismusfinanzierung · Risiko-Selbstcheck für die Maklerakte',
      ),
      description: t(
        'docs.public.checkliste.body',
        'Strukturierter 11-Punkte-Risikoscreening nach § 8a–8e RAO mit Auswertungsblock und drei legalen Anhängen (Anlage I, II, III).',
      ),
      icon: <ShieldCheck className="h-5 w-5" />,
      fillablePdfUrl: '/docs/4M_Checkliste_GW_TF_fillable.pdf',
      fillablePdfName: '4M_Checkliste_GW_TF_fillable.pdf',
      onlineRoute: '/dokumente/checkliste',
    },
    {
      id: 'kyc',
      title: t('docs.public.kyc.title', 'KYC-Formular für natürliche Personen'),
      subtitle: t(
        'docs.public.kyc.subtitle',
        'Profil der Geschäftsbeziehung · Pflichtdatenerhebung gemäß GW/TF-Bestimmungen',
      ),
      description: t(
        'docs.public.kyc.body',
        'Vollständige Datenerhebung zum wirtschaftlich Berechtigten — Identität, Hintergrund, Herkunft der Mittel, PEP-Status, Bestätigungen mit Unterschrift, plus rechtliche Begriffserklärungen.',
      ),
      icon: <ClipboardList className="h-5 w-5" />,
      fillablePdfUrl: '/docs/4M_KYC_Formular_fillable.pdf',
      fillablePdfName: '4M_KYC_Formular_fillable.pdf',
      onlineRoute: '/dokumente/kyc',
    },
  ];

  return (
    <section className="space-y-8">
      <header>
        <h2 className="flex items-center gap-2 font-display text-2xl text-ink">
          <FileText className="h-6 w-6 text-[hsl(var(--brand-gold-deep))]" />
          {t('docs.public.title', 'Öffentliche Dokumente')}
        </h2>
        <p className="text-sm text-ink-3">
          {t(
            'docs.public.subtitle',
            'Leitfäden, Schulungsunterlagen und Pflichtformulare · für alle Mitarbeiter · ansehen oder herunterladen.',
          )}
        </p>
      </header>

      {/* ── Section 1: Leitfäden & Schulungsunterlagen ── */}
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 font-display text-lg text-ink">
          <BookOpen className="h-5 w-5 text-[hsl(var(--brand-gold-deep))]" />
          {t('docs.public.section_guides', 'Leitfäden & Schulungsunterlagen')}
        </h3>

        <div className="fm-card border-s-[3px] border-s-[hsl(var(--brand-gold))] p-5">
          <p className="text-sm text-ink-2 leading-relaxed">
            {t(
              'docs.public.guides_intro',
              'Verkaufs- und Gesprächsleitfäden für Makler und Call-Center — direkt im Browser ansehen oder als PDF herunterladen und ausdrucken.',
            )}
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {guides.map((g) => (
            <article
              key={g.id}
              className="fm-card p-5 flex flex-col gap-4 hover:border-ink hover:shadow-soft transition"
            >
              <header className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--brand-gold)/0.10)] text-[hsl(var(--brand-gold-deep))] shrink-0">
                  {g.icon}
                </span>
                <div className="min-w-0">
                  <h4 className="font-display text-lg text-ink leading-tight">{g.title}</h4>
                  <p className="mt-0.5 text-xs uppercase tracking-wide text-ink-3">{g.subtitle}</p>
                </div>
              </header>

              <p className="text-sm text-ink-2 leading-relaxed">{g.description}</p>

              <div className="mt-auto flex flex-wrap gap-2 pt-2">
                <a
                  href={g.pdfUrl}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 rounded-md bg-[hsl(var(--brand-gold))] px-3 py-2 text-sm font-semibold text-[#1a1305] shadow-soft transition hover:bg-[hsl(var(--brand-gold-deep))] hover:text-white"
                >
                  <FileText className="h-4 w-4" />
                  {t('docs.public.open_in_browser', 'Im Browser öffnen')}
                </a>
                <a
                  href={g.pdfUrl}
                  download={g.pdfName}
                  className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
                >
                  <Download className="h-4 w-4" />
                  {t('docs.public.download', 'PDF herunterladen')}
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* ── Section 2: Geldwäsche-Pflichtformulare ── */}
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 font-display text-lg text-ink">
          <ShieldCheck className="h-5 w-5 text-[hsl(var(--brand-gold-deep))]" />
          {t('docs.public.section_forms', 'Geldwäsche-Pflichtformulare')}
        </h3>

        {/* Intro card */}
        <div className="fm-card border-s-[3px] border-s-[hsl(var(--brand-gold))] p-5">
          <p className="text-sm text-ink-2 leading-relaxed">
            {t(
              'docs.public.intro',
              'Jedes Dokument unten ist als ausfüllbares PDF verfügbar — Sie können es auf jedem Gerät (Desktop, Tablet, Smartphone) herunterladen, direkt im PDF-Reader ausfüllen, lokal speichern und teilen. Felder, Kontrollkästchen und Auswahlfelder sind interaktiv.',
            )}
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--brand-gold-deep))]">
            <Sparkles className="h-3.5 w-3.5" />
            {t(
              'docs.public.online_available',
              'Tipp: „Online ausfüllen" führt Sie Schritt-für-Schritt durch das Formular und erstellt am Ende automatisch ein bereits ausgefülltes PDF zum Download.',
            )}
          </p>
        </div>

        {/* Document cards */}
        <div className="grid gap-4 lg:grid-cols-2">
          {documents.map((doc) => (
            <article
              key={doc.id}
              className="fm-card p-5 flex flex-col gap-4 hover:border-ink hover:shadow-soft transition"
            >
              <header className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--brand-gold)/0.10)] text-[hsl(var(--brand-gold-deep))] shrink-0">
                  {doc.icon}
                </span>
                <div className="min-w-0">
                  <h4 className="font-display text-lg text-ink leading-tight">{doc.title}</h4>
                  <p className="mt-0.5 text-xs uppercase tracking-wide text-ink-3">
                    {doc.subtitle}
                  </p>
                </div>
              </header>

              <p className="text-sm text-ink-2 leading-relaxed">{doc.description}</p>

              <div className="mt-auto flex flex-wrap gap-2 pt-2">
                <Link
                  to={doc.onlineRoute}
                  className="inline-flex items-center gap-2 rounded-md bg-[hsl(var(--brand-gold))] px-3 py-2 text-sm font-semibold text-[#1a1305] shadow-soft transition hover:bg-[hsl(var(--brand-gold-deep))] hover:text-white"
                >
                  <PencilLine className="h-4 w-4" />
                  {t('docs.public.fill_online', 'Online ausfüllen')}
                </Link>
                <a
                  href={doc.fillablePdfUrl}
                  download={doc.fillablePdfName}
                  className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
                >
                  <Download className="h-4 w-4" />
                  {t('docs.public.download_fillable', 'Leeres PDF herunterladen')}
                </a>
                <a
                  href={doc.fillablePdfUrl}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm font-medium text-ink-2 hover:bg-paper-2"
                >
                  <FileText className="h-4 w-4" />
                  {t('docs.public.open_in_browser', 'Im Browser öffnen')}
                </a>
              </div>

              <p className="text-[11px] text-ink-3">
                {t(
                  'docs.public.fillable_hint',
                  'Eingabefelder, Kontrollkästchen und Auswahlfelder sind interaktiv — direkt im PDF-Reader ausfüllen und speichern.',
                )}
              </p>
            </article>
          ))}
        </div>

        {/* Legal footnote */}
        <p className="text-[11px] text-ink-3 leading-relaxed">
          {t(
            'docs.public.legal',
            'Die Formulare entsprechen den Vorgaben zur Geldwäsche- und Terrorismusfinanzierungsprävention nach den §§ 8a–8e RAO. Bei Fragen wenden Sie sich an office@4m-immo.at oder den Administrator.',
          )}
        </p>
      </div>
    </section>
  );
}
